import { useSelectors } from '@exodus/headless-react';
import usePrevious from '@exodus/react-hooks/common/usePrevious';
import useDebouncedCallback from '@exodus/react-hooks/common/useDebouncedCallback.js';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import useFromAmount from './use-from-amount.js';
import useFromAsset from './use-from-asset.js';
import useFromWalletAccount from './use-from-wallet-account.js';
import usePrefilledAmountId from './use-prefilled-amount-id.js';
import useSlippage from './use-slippage.js';
import useToAsset from './use-to-asset.js';
import useToWalletAccount from './use-to-wallet-account.js';
import useSwapMode from './use-swap-mode.js';
import useToAmount from './use-to-amount.js';
import useIsExchanging from './use-is-exchanging.js';
import { useQuotesManager } from './use-quotes-manager.js';
export const getQuoteId = (params) => {
    const { fromAssetName, toAssetName, amount, swapMode = 'ExactIn' } = params;
    return `${fromAssetName}-${toAssetName}-${amount.toDefaultString({ unit: false })}-${swapMode}`;
};
const useQuote = () => {
    const selectors = useSelectors();
    const [error, setError] = useState(null);
    const [prefilledAmountId] = usePrefilledAmountId();
    const [fromAsset] = useFromAsset();
    const [toAsset] = useToAsset();
    const [fromAmount] = useFromAmount();
    const [fromWalletAccount] = useFromWalletAccount();
    const [toWalletAccount] = useToWalletAccount();
    const [slippage] = useSlippage();
    const [swapMode] = useSwapMode();
    const [toAmount] = useToAmount();
    const isExchanging = useIsExchanging();
    const quotesManager = useQuotesManager();
    const isSendAll = prefilledAmountId === 'max';
    const quotes = useSelector(selectors.exchange.quotes);
    const amount = swapMode === 'ExactIn' ? fromAmount : toAmount;
    const quoteId = getQuoteId({
        amount,
        fromAssetName: fromAsset.name,
        toAssetName: toAsset.name,
        swapMode,
    });
    useEffect(() => {
        setError(null);
    }, [fromAsset, toAsset]);
    const pollBestQuote = useCallback(async ({ amount, onError, abortSignal, }) => quotesManager.pollBestQuote({
        quoteParams: {
            amount,
            fromAssetName: fromAsset.name,
            toAssetName: toAsset.name,
            slippage,
            quoteId,
            fromWalletAccount: fromWalletAccount.toString(),
            toWalletAccount: toWalletAccount.toString(),
            isSendAll,
            swapMode: swapMode,
        },
        onError,
        abortSignal,
    }), [
        quotesManager,
        fromAsset.name,
        toAsset.name,
        slippage,
        quoteId,
        fromWalletAccount,
        toWalletAccount,
        isSendAll,
        swapMode,
    ]);
    const debouncedPollBestQuote = useDebouncedCallback(pollBestQuote, 300);
    const finalPollBestQuote = prefilledAmountId === null ? debouncedPollBestQuote : pollBestQuote;
    useEffect(() => {
        const shouldSkip = isExchanging || amount.isZero;
        if (shouldSkip)
            return;
        const abortController = new AbortController();
        void finalPollBestQuote({
            amount,
            onError: (err) => {
                setError(err.toString());
            },
            abortSignal: abortController.signal,
        });
        return () => abortController.abort();
    }, [finalPollBestQuote, swapMode, isExchanging, amount]);
    const { quote, loading: loadingQuote = true } = quotes[quoteId] ?? {};
    const _quote = useMemo(() => (quote ? { ...quote, fee: quote.exchangeFeeData?.feeOpts?.fee, id: quoteId } : null), [quote, quoteId]);
    const lastRateId = usePrevious(quote?.rateId);
    useEffect(() => {
        if (!loadingQuote && !quote) {
            setError('No quote available');
        }
        else {
            setError(null);
        }
    }, [loadingQuote, quote]);
    const isLoadingQuote = loadingQuote &&
        ((swapMode === 'ExactIn' && fromAmount.isPositive) ||
            (swapMode === 'ExactOut' && toAmount.isPositive));
    const loading = quote?.rateId === lastRateId ? false : isLoadingQuote;
    return [_quote, error, loading];
};
export default useQuote;
